import { SECTION_FLAGS, BALANCE_ONBOARDING_REQUIRED_FLAGS, } from './permissions.js';
export const HELP_CATEGORY_SLUGS = [
    'getting-started',
    'dashboard',
    'accounts',
    'forecast',
    'documents',
    'document-payment-schedules',
    'payments',
    'registry',
    'settings',
    'warehouse',
    'integrations',
    'advisor-panel',
    'troubleshooting',
    'commesse',
    'service-contracts',
    'work-reports',
    'hotel',
    'machines',
];
export const HELP_CATEGORY_FEATURE_GATES = {
    warehouse: 'warehouseEnabled',
    commesse: 'commesseEnabled',
    'service-contracts': 'serviceContractsEnabled',
    'work-reports': 'workReportsEnabled',
    hotel: 'hotelEnabled',
    machines: 'machinesEnabled',
    'advisor-panel': 'isAdvisor',
};
/**
 * Section flags of the tenant modules in trial: an article that requires one
 * of them (category default, table override or frontmatter) also requires the
 * module, wherever the article lives. Never bypassed by OWNER.
 */
export const HELP_SECTION_FEATURE_GATES = {
    canAccessWarehouse: 'warehouseEnabled',
    canAccessCommesse: 'commesseEnabled',
    canAccessServiceContracts: 'serviceContractsEnabled',
    canAccessWorkReports: 'workReportsEnabled',
    canAccessHotel: 'hotelEnabled',
    canAccessMachines: 'machinesEnabled',
};
function passesFeatureGate(gate, context) {
    if (!gate)
        return true;
    if (gate === 'workReportsEnabled') {
        return (context.workReportsEnabled === true &&
            context.serviceContractsEnabled === true);
    }
    return context[gate] === true;
}
/** Feature availability is never bypassed by an OWNER. Missing context fails closed. */
export function passesHelpCategoryFeatureGate(category, context) {
    return passesFeatureGate(HELP_CATEGORY_FEATURE_GATES[category], context);
}
/** Module gates implied by the section flags an article requires. */
export function getHelpArticleFeatureGates(article) {
    const gates = getHelpArticleRequiredPermissions(article)
        .map((flag) => HELP_SECTION_FEATURE_GATES[flag])
        .filter((gate) => gate !== undefined);
    return [...new Set(gates)];
}
/** Category gate plus the module gates of the sections the article requires. */
export function passesHelpArticleFeatureGate(article, context) {
    return (passesHelpCategoryFeatureGate(article.category, context) &&
        getHelpArticleFeatureGates(article).every((gate) => passesFeatureGate(gate, context)));
}
/**
 * Default permission required to access any article in a given category.
 * `null` means the category is always visible (no permission required).
 */
export const HELP_CATEGORY_PERMISSIONS = {
    commesse: 'canAccessCommesse',
    'service-contracts': 'canAccessServiceContracts',
    'work-reports': 'canAccessWorkReports',
    hotel: 'canAccessHotel',
    machines: 'canAccessMachines',
    accounts: 'canAccessAccounts',
    forecast: 'canAccessForecast',
    documents: 'canAccessDocuments',
    registry: 'canAccessRegistry',
    'document-payment-schedules': 'canAccessPaymentSchedules',
    settings: 'canAccessSettings',
    payments: 'canAccessPayments',
    'getting-started': null,
    dashboard: null,
    troubleshooting: null,
    // Feature-gated categories also require their section permission.
    // Integrations is always visible at the category
    // level; single connector articles gate via overrides below.
    warehouse: 'canAccessWarehouse',
    integrations: null,
    'advisor-panel': null,
};
/**
 * Vista delle distinte (schede RIBA e Anticipi di Pagamenti): la stessa terna
 * del controller delle distinte e della pagina Pagamenti.
 */
const DISTINTE_VIEW_FLAGS = [
    'canAccessPayments',
    'canAccessAccounts',
    'canAccessDocuments',
];
/**
 * Operazioni sulle distinte (creare, approvare, chiudere, annullare,
 * ripresentare): la vista più scrittura ed esecuzione dei pagamenti, come il
 * controller e `canChangeDistinta` della webapp.
 */
const DISTINTE_ACTION_FLAGS = [
    ...DISTINTE_VIEW_FLAGS,
    'canWrite',
    'canExecutePayments',
];
/**
 * Movimenti della distinta (confermare, ignorare, scollegare): la vista più
 * la sola scrittura, come le rotte `*-movement` del controller.
 */
const DISTINTE_MOVEMENT_FLAGS = [
    ...DISTINTE_VIEW_FLAGS,
    'canWrite',
];
/**
 * Pagina Pagamenti nel suo insieme: il denominatore comune delle schede
 * distinte (Conti + Documenti) e Bonifici (Conti + esecuzione pagamenti).
 */
const PAYMENTS_PAGE_FLAGS = [
    'canAccessPayments',
    'canAccessAccounts',
];
/**
 * Scheda Bonifici di Pagamenti: la pagina la mostra solo con Conti e
 * l'esecuzione dei pagamenti. La sezione Pagamenti va ripetuta perché un flag
 * di sezione nell'override sostituisce il default della categoria.
 */
const SEPA_TRANSFERS_TAB_FLAGS = [
    'canAccessPayments',
    'canAccessAccounts',
    'canExecutePayments',
];
/**
 * Per-article permission overrides. These flags are added (AND) on top of the
 * category default. If an article requires a specific action flag beyond the
 * category section flag, list it here.
 */
export const HELP_ARTICLE_PERMISSION_OVERRIDES = {
    // Settings — connectors / admin actions
    'settings/cassetto-fiscale': ['canConnectCassettoFiscale'],
    'settings/procedura-incarico-cassetto-fiscale': ['canConnectCassettoFiscale'],
    'settings/gestire-utenti-e-ruoli': ['canManageUsers'],
    'settings/come-funzionano-i-permessi-a-flag': ['canManageUsers'],
    'settings/usare-lanteprima-permessi': ['canManageUsers'],
    'settings/come-gestire-abbonamento-e-fatturazione': ['canManageBilling'],
    // Settings — invoicing config gated by billing management
    'settings/come-gestire-le-tabelle-di-supporto': ['canManageBilling'],
    'settings/come-personalizzare-laspetto-della-fattura': ['canManageBilling'],
    'settings/registrare-indirizzo-telematico-sdi': [],
    // Accounts — bank connections
    'accounts/come-gestire-una-riconnessione-fallita': ['canConnectBankAccounts'],
    'accounts/come-leggere-un-conto-sincronizzato': ['canConnectBankAccounts'],
    'accounts/gestire-override-e-campi-bloccati': [
        'canConnectBankAccounts',
        'canWrite',
    ],
    // Accounts — write operations
    'accounts/come-applicare-una-categoria-a-un-movimento': ['canWrite'],
    'accounts/come-creare-una-ricorrenza-da-un-movimento': ['canWrite'],
    'accounts/come-gestire-un-trasferimento-interno': ['canWrite'],
    'accounts/come-importare-movimenti-bancari': ['canWrite'],
    'accounts/come-usare-i-suggerimenti-su-movimenti-simili': ['canWrite'],
    'accounts/creare-un-conto-manuale': ['canWrite'],
    'accounts/importare-lexport-paypal': ['canWrite'],
    'accounts/manuale-form-movimento': ['canWrite'],
    // Accounts — saldi, fido, disponibile e prima nota: allineati a
    // `canViewAccountBalances` (gli articoli sui movimenti che nominano il saldo
    // di passaggio restano visibili).
    'accounts/come-leggere-saldo-contabile-e-disponibile': [
        'canViewAccountBalances',
    ],
    'accounts/come-verificare-saldi-e-movimenti': ['canViewAccountBalances'],
    'accounts/come-esportare-la-prima-nota': [
        'canExport',
        'canViewAccountBalances',
    ],
    // Accounts — reconciliation
    'accounts/come-collegare-un-movimento-a-una-riba': ['canReconcile'],
    'accounts/come-partire-dai-movimenti-per-riconciliare-una-scadenza': [
        'canReconcile',
    ],
    'accounts/come-usare-la-bulk-reconciliation': ['canReconcile'],
    // Payments — viewing distinte differs from executing payment actions
    'payments/come-capire-se-agire-in-pagamenti-o-scadenzario': [
        'canExecutePayments',
    ],
    'payments/come-gestire-un-pagamento-fallito': SEPA_TRANSFERS_TAB_FLAGS,
    'payments/come-leggere-gli-stati-dei-pagamenti': PAYMENTS_PAGE_FLAGS,
    'payments/gestire-bonifici-e-trasferimenti': SEPA_TRANSFERS_TAB_FLAGS,
    'payments/manuale-tab-anticipi': DISTINTE_VIEW_FLAGS,
    'payments/manuale-tab-riba': DISTINTE_VIEW_FLAGS,
    'payments/manuale-tab-transfers': SEPA_TRANSFERS_TAB_FLAGS,
    'payments/monitorare-riba-e-anticipi': DISTINTE_VIEW_FLAGS,
    'payments/come-esportare-i-pagamenti': ['canExecutePayments', 'canExport'],
    'payments/come-ritentare-un-pagamento-sepa': SEPA_TRANSFERS_TAB_FLAGS,
    'payments/rilevare-riba-dai-movimenti': DISTINTE_ACTION_FLAGS,
    // Documents — write operations
    'documents/come-applicare-una-categoria-a-un-documento': ['canWrite'],
    'documents/come-importare-un-xml-fatturapa': ['canWrite'],
    'documents/come-lavorare-sui-documenti-acquisto': ['canWrite'],
    'documents/come-lavorare-sui-documenti-vendita': ['canWrite'],
    'documents/come-usare-i-suggerimenti-su-documenti-simili': ['canWrite'],
    'documents/come-usare-il-bulk-delete-nei-tab-supportati': ['canWrite'],
    'documents/registrare-un-documento': ['canWrite'],
    'documents/trasformare-una-proforma-in-fattura': ['canWrite'],
    'documents/correggere-una-fattura-scartata': ['canWrite'],
    'documents/documenti-fiscali-speciali': ['canWrite'],
    'documents/compensare-una-nota-di-credito': ['canWrite', 'canReconcile'],
    // Sezione Commesse in più: Documenti va ripetuto perché un flag di sezione
    // nell'override sostituisce il default della categoria. Il gate del modulo
    // deriva dal flag di sezione (HELP_SECTION_FEATURE_GATES).
    'documents/collegare-documenti-a-una-commessa': [
        'canAccessDocuments',
        'canAccessCommesse',
    ],
    'documents/come-creare-un-ddt': ['canAccessDeliveryNotes', 'canWrite'],
    'documents/come-trasformare-ddt-in-fattura': [
        'canAccessDeliveryNotes',
        'canAccessDocuments',
        'canWrite',
    ],
    'documents/come-creare-un-ordine': ['canAccessOrders', 'canWrite'],
    'documents/come-gestire-debiti-e-pa-obligations': [
        'canAccessAdministration',
        'canWrite',
    ],
    'documents/come-gestire-documenti-payroll': [
        'canAccessAdministration',
        'canWrite',
    ],
    // Documents — reconciliation
    'documents/come-partire-dai-documenti-per-riconciliare-una-scadenza': [
        'canReconcile',
    ],
    'documents/come-usare-la-riconciliazione-fatture-estere': ['canReconcile'],
    // Forecast — write operations
    'forecast/come-correggere-duplicazioni-nel-forecast': ['canWrite'],
    'forecast/gestire-pianificazioni-e-ricorrenze': ['canWrite'],
    'forecast/leggere-il-dscr': ['canAccessDscr', 'canViewAccountBalances'],
    // Forecast — articoli che spiegano saldo, liquidità o fido (vista di cassa,
    // anche nel confronto tra scenari). Il Previsionale non richiede i saldi:
    // pianificazioni, ricorrenze e manuale restano col solo canAccessForecast.
    'forecast/come-usare-scenari-e-pianificazioni': [
        'canWrite',
        'canViewAccountBalances',
    ],
    'forecast/come-leggere-il-forecast-aggregato': ['canViewAccountBalances'],
    'forecast/come-leggere-la-vista-giornaliera': ['canViewAccountBalances'],
    'forecast/quando-aggiornare-una-ricorrenza-vs-crearne-una-nuova': [
        'canWrite',
    ],
    // Registry — write and export
    'registry/come-esportare-controparti': ['canExport'],
    'registry/come-esportare-dipendenti': ['canExport'],
    'registry/come-evitare-duplicazioni-anagrafiche': ['canWrite'],
    'registry/come-preparare-unanagrafica-per-luso-nei-documenti': ['canWrite'],
    'registry/gestire-controparti-e-dipendenti': ['canWrite'],
    'registry/manuale-tab-prodotti': ['canAccessProducts'],
    'registry/come-creare-un-prodotto': ['canAccessProducts', 'canWrite'],
    'registry/come-usare-il-lookup-prodotto-in-fattura': ['canAccessProducts'],
    'registry/come-esportare-prodotti': ['canAccessProducts', 'canExport'],
    // Document Payment Schedules — consultare le distinte richiede gli stessi
    // permessi di vista delle schede RIBA e Anticipi (Pagamenti + Conti +
    // Documenti, come il controller delle distinte); le azioni operative
    // (creare, approvare, chiudere, annullare, ripresentare) chiedono anche
    // `canWrite` + `canExecutePayments`, i movimenti della distinta `canWrite`.
    'document-payment-schedules/manuale-distinte-riba': DISTINTE_VIEW_FLAGS,
    'document-payment-schedules/come-leggere-il-dettaglio-di-una-distinta': DISTINTE_VIEW_FLAGS,
    'document-payment-schedules/come-leggere-lesposizione-residua-di-una-distinta': DISTINTE_VIEW_FLAGS,
    // Document Payment Schedules — payments execution
    'document-payment-schedules/distinta-riba-con-anticipo-sbf': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/modificare-e-scaricare-una-distinta': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/registrare-insoluti-e-storni': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-avviare-un-pagamento-sepa-da-una-scadenza': [
        'canExecutePayments',
    ],
    'document-payment-schedules/come-creare-una-distinta-riba': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-creare-una-distinta-anticipo-fatture': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-approvare-una-distinta': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-chiudere-e-riaprire-una-distinta': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-annullare-una-distinta': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-ripresentare-una-distinta-riba': DISTINTE_ACTION_FLAGS,
    'document-payment-schedules/come-usare-il-workspace-movimenti-di-una-distinta': DISTINTE_MOVEMENT_FLAGS,
    // Document Payment Schedules — reconciliation
    'document-payment-schedules/riconciliare-scadenze': ['canReconcile'],
    'document-payment-schedules/come-annullare-un-collegamento-errato': [
        'canReconcile',
    ],
    'document-payment-schedules/come-usare-il-dialog-allocazione-per-schedule': [
        'canReconcile',
    ],
    'document-payment-schedules/come-usare-il-workspace-movimenti': [
        'canReconcile',
    ],
    // Document Payment Schedules — write
    'document-payment-schedules/come-applicare-una-categoria-da-scadenzario': [
        'canWrite',
    ],
    'document-payment-schedules/come-creare-una-scadenza': ['canWrite'],
    // Dashboard — AI chat articles
    'dashboard/come-usare-la-chat-ai-finance': ['canUseAI'],
    'dashboard/come-usare-la-chat-ai-help': ['canUseAI'],
    'dashboard/usare-lassistente-ai': ['canUseAI'],
    'dashboard/come-passare-dalla-chat-piccola-alla-chat-grande': ['canUseAI'],
    // Dashboard — salvare la disposizione delle card scrive nelle impostazioni
    // del tenant.
    'dashboard/personalizzare-la-dashboard': ['canWrite', 'canAccessSettings'],
    // Commesse — il riparto legge e scrive i costi del personale
    // (Amministrazione): la sezione Commesse va ripetuta.
    'commesse/ripartire-il-costo-del-personale': [
        'canAccessCommesse',
        'canAccessAdministration',
    ],
    'settings/configurare-solleciti-e-fatture-ricorrenti': ['canManageBilling'],
    'settings/usare-i-valori-predefiniti-nei-form': ['canManageBilling'],
    'documents/inviare-piu-fatture-allo-sdi': ['canWrite'],
    'integrations/importare-scadenzario-genya': ['canAccessDocuments'],
    'integrations/gestire-api-e-webhook': [
        'canManageApiKeys',
        'canViewAccountBalances',
    ],
    // Getting started — guided flows that target specific features
    'getting-started/come-completare-lonboarding-dei-conti': [
        ...BALANCE_ONBOARDING_REQUIRED_FLAGS,
    ],
    'getting-started/come-esportare-dati-dai-moduli': ['canExport'],
    // Integrations — connector-specific permission flags
    'integrations/come-connettere-mcp-claude': ['canConnectMcp'],
    'integrations/come-gestire-la-policy-client-mcp': ['canConnectMcp'],
    'integrations/come-connettere-jet-hr': ['canConnectJetHr'],
    'integrations/cosa-sincronizza-jet-hr': ['canConnectJetHr'],
    'integrations/collegamento-reviso': ['canConnectReviso'],
    'integrations/collegamento-paypal': ['canConnectPaypal'],
    // Machines — approvare una verifica: flag di approvazione + scrittura.
    'machines/approvare-una-verifica-e-gestire-le-anomalie': [
        'canApproveMachineChecks',
        'canWrite',
    ],
};
const INDEPENDENT_SETTINGS_ARTICLES = new Set([
    'settings/gestire-utenti-e-ruoli',
    'settings/usare-lanteprima-permessi',
    'settings/come-funzionano-i-permessi-a-flag',
    'settings/come-gestire-abbonamento-e-fatturazione',
    'settings/come-personalizzare-laspetto-della-fattura',
    'settings/registrare-indirizzo-telematico-sdi',
    'settings/come-scegliere-la-modalita-di-fatturazione',
    'settings/cassetto-fiscale',
    'settings/procedura-incarico-cassetto-fiscale',
    'settings/come-configurare-le-preferenze-personali',
    'settings/configurare-solleciti-e-fatture-ricorrenti',
]);
/**
 * Resolves the full list of permission flags required to access an article,
 * combining the category default with article-specific overrides (from the
 * central table and from optional frontmatter overrides).
 */
export function getHelpArticleRequiredPermissions(input) {
    const categoryPermission = INDEPENDENT_SETTINGS_ARTICLES.has(input.slug)
        ? null
        : (HELP_CATEGORY_PERMISSIONS[input.category] ?? null);
    const tableOverrides = HELP_ARTICLE_PERMISSION_OVERRIDES[input.slug] ?? [];
    const frontmatterOverrides = (input.frontmatterOverrides ??
        []);
    const flags = new Set([
        ...tableOverrides,
        ...frontmatterOverrides,
    ]);
    const hasSectionOverride = [...flags].some((flag) => SECTION_FLAGS.includes(flag));
    if (categoryPermission && !hasSectionOverride) {
        flags.add(categoryPermission);
    }
    return [...flags];
}
/**
 * Checks whether a user with the given permission flags can access an article.
 * OWNER bypasses all checks — pass `isOwner: true` to short-circuit.
 */
export function canUserAccessHelpArticle(input) {
    if (input.isOwner) {
        return true;
    }
    return input.requiredPermissions.every((flag) => input.permissions[flag] === true);
}
const INVOICING_ENTRY_ARTICLES = new Set([
    'settings/come-scegliere-la-modalita-di-fatturazione',
    'settings/come-personalizzare-laspetto-della-fattura',
    'settings/registrare-indirizzo-telematico-sdi',
    'settings/configurare-solleciti-e-fatture-ricorrenti',
]);
/** Complete policy for rendered help and retrieval, including OR entry points. */
export function canUserAccessHelpContent(article, context) {
    if (!passesHelpArticleFeatureGate(article, context))
        return false;
    if (context.isOwner)
        return true;
    if (INVOICING_ENTRY_ARTICLES.has(article.slug) &&
        context.permissions.canAccessSettings !== true &&
        context.permissions.canConnectCassettoFiscale !== true)
        return false;
    return canUserAccessHelpArticle({
        requiredPermissions: getHelpArticleRequiredPermissions(article),
        isOwner: false,
        permissions: context.permissions,
    });
}
//# sourceMappingURL=help-permissions.js.map